import React, { useState, useEffect } from "react";
import { Link, useNavigate } from 'react-router-dom'; // ✅ CHANGED: Added useNavigate
import "./WishList.css";


const WishList = () => {
  const [wishlist, setWishlist] = useState([]);
  const navigate = useNavigate(); // ✅ ADDED: Initialize navigate


  useEffect(() => {
    loadWishlist();

    // ✅ Listen for wishlist updates from Voice Assistant or other sources
    const handleWishlistUpdate = () => {
      console.log('🔄 Wishlist updated - reloading data...');
      loadWishlist();
    };

    // Listen for custom wishlist update event
    window.addEventListener('wishlistUpdated', handleWishlistUpdate);

    // Listen for storage event (works across browser tabs)
    const handleStorageChange = (e) => {
      if (e.key === 'wishlist') {
        console.log('🔄 Storage changed - reloading wishlist...');
        handleWishlistUpdate();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Cleanup listeners when component unmounts
    return () => {
      window.removeEventListener('wishlistUpdated', handleWishlistUpdate);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);


  const loadWishlist = () => {
    const storedWishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
    setWishlist(storedWishlist);
  };


  const handleRemoveItem = (index) => {
    const updatedWishlist = wishlist.filter((_, i) => i !== index);
    localStorage.setItem('wishlist', JSON.stringify(updatedWishlist));
    setWishlist(updatedWishlist);
  };


  const handleUpdateQuantity = (index, delta) => {
    const updatedWishlist = [...wishlist];
    const newQuantity = updatedWishlist[index].quantity + delta;

    if (newQuantity >= 1 && newQuantity <= 10) {
      updatedWishlist[index].quantity = newQuantity;
      localStorage.setItem('wishlist', JSON.stringify(updatedWishlist));
      setWishlist(updatedWishlist);
    }
  };


  const getTotalPrice = () => {
    return wishlist.reduce((total, item) => {
      return total + (parseFloat(item.price) * item.quantity);
    }, 0).toFixed(2);
  };


  const getTotalItems = () => {
    return wishlist.reduce((total, item) => total + item.quantity, 0);
  };


  const isEmpty = wishlist.length === 0;


  const EmptyWishlist = () => (
    <div className="empty-wishlist-container">
      <div className="empty-icon">
        <svg width="100" height="100" viewBox="0 0 24 24" fill="none">
          <path d="M20.84 4.61C20.3292 4.099 19.7228 3.69364 19.0554 3.41708C18.3879 3.14052 17.6725 2.99817 16.95 2.99817C16.2275 2.99817 15.5121 3.14052 14.8446 3.41708C14.1772 3.69364 13.5708 4.099 13.06 4.61L12 5.67L10.94 4.61C9.9083 3.57831 8.50903 2.99871 7.05 2.99871C5.59096 2.99871 4.19169 3.57831 3.16 4.61C2.1283 5.64169 1.54871 7.04097 1.54871 8.5C1.54871 9.95903 2.1283 11.3583 3.16 12.39L4.22 13.45L12 21.23L19.78 13.45L20.84 12.39C21.351 11.8792 21.7564 11.2728 22.0329 10.6054C22.3095 9.93789 22.4518 9.22248 22.4518 8.5C22.4518 7.77752 22.3095 7.06211 22.0329 6.39464C21.7564 5.72718 21.351 5.12082 20.84 4.61Z"
            stroke="currentColor" strokeWidth="2" />
        </svg>
      </div>
      <h3>Your Wishlist is Empty</h3>
      <p>Add products you love to your wishlist!</p>
      <Link to="/" className="go-back-btn">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" />
        </svg>
        Start Shopping
      </Link>
    </div>
  );


  const FilledWishList = () => (
    <>
      <div className="wishlist-items">
        {wishlist.map((item, index) => (
          <div key={index} className="wishlist-item-card">
            <div className="item-model-section">
              <model-viewer
                src={item.modelSrc}
                ios-src={item.iOSSrc}
                alt={item.name}
                camera-controls
                ar
                auto-rotate
                shadow-intensity="1"
                style={{ width: '180px', height: '180px', borderRadius: '12px' }}
              >
                {item.annotations?.map((annotation, idx) => (
                  <button
                    key={idx}
                    slot={annotation.slot}
                    data-position={annotation.position}
                    data-normal={annotation.normal}
                    data-orbit={annotation.orbit}
                    data-target={annotation.target}
                    className="annotation-button"
                  />
                ))}
              </model-viewer>
            </div>


            <div className="item-info-section">
              <div className="item-header">
                <h3>{item.name}</h3>
                <div className="item-rating">
                  {'⭐'.repeat(Math.floor(parseFloat(item.rating || 0)))}
                  <span>{item.rating}</span>
                </div>
              </div>


              <div className="item-meta">
                <span className="item-category">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="2" />
                  </svg>
                  {item.category}
                </span>
                <span className="item-color">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                  </svg>
                  {item.selectedColor}
                </span>
              </div>


              <p className="item-description">{item.description?.substring(0, 80)}...</p>


              <div className="item-quantity">
                <span className="qty-label">Quantity:</span>
                <div className="qty-controls">
                  <button
                    className="qty-btn"
                    onClick={() => handleUpdateQuantity(index, -1)}
                    disabled={item.quantity <= 1}
                  >
                    −
                  </button>
                  <span className="qty-value">{item.quantity}</span>
                  <button
                    className="qty-btn"
                    onClick={() => handleUpdateQuantity(index, 1)}
                    disabled={item.quantity >= 10}
                  >
                    +
                  </button>
                </div>
              </div>


              <div className="item-price-section">
                <div className="price-info">
                  <span className="price-label">Price:</span>
                  <span className="price-value">${item.price}</span>
                </div>
                <div className="total-info">
                  <span className="total-label">Total:</span>
                  <span className="total-value">${(parseFloat(item.price) * item.quantity).toFixed(2)}</span>
                </div>
              </div>


              <div className="item-actions">
                <button
                  className="remove-btn"
                  onClick={() => handleRemoveItem(index)}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
                      stroke="currentColor" strokeWidth="2" />
                  </svg>
                  Remove
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>


      <div className="wishlist-summary">
        <div className="summary-card">
          <h3>Order Summary</h3>
          <div className="summary-row">
            <span>Total Items:</span>
            <span>{getTotalItems()}</span>
          </div>
          <div className="summary-row">
            <span>Subtotal:</span>
            <span>${getTotalPrice()}</span>
          </div>
          <div className="summary-row">
            <span>Shipping:</span>
            <span className="free">Free</span>
          </div>
          <div className="summary-divider"></div>
          <div className="summary-row total">
            <span>Total:</span>
            <span>${getTotalPrice()}</span>
          </div>
          <button
            className="proceed-payment-btn"
            onClick={() => navigate('/checkout')} // ✅ ADDED: Navigate to checkout
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2" />
              <path d="M2 10h20" stroke="currentColor" strokeWidth="2" />
            </svg>
            Proceed to Payment
          </button>
          <Link to="/" className="continue-shopping-link">
            ← Continue Shopping
          </Link>
        </div>
      </div>
    </>
  );


  return (
    <div className="wishlist-container">
      <div className="wishlist-header">
        <h1>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
            <path d="M20.84 4.61C20.3292 4.099 19.7228 3.69364 19.0554 3.41708C18.3879 3.14052 17.6725 2.99817 16.95 2.99817C16.2275 2.99817 15.5121 3.14052 14.8446 3.41708C14.1772 3.69364 13.5708 4.099 13.06 4.61L12 5.67L10.94 4.61C9.9083 3.57831 8.50903 2.99871 7.05 2.99871C5.59096 2.99871 4.19169 3.57831 3.16 4.61C2.1283 5.64169 1.54871 7.04097 1.54871 8.5C1.54871 9.95903 2.1283 11.3583 3.16 12.39L4.22 13.45L12 21.23L19.78 13.45L20.84 12.39C21.351 11.8792 21.7564 11.2728 22.0329 10.6054C22.3095 9.93789 22.4518 9.22248 22.4518 8.5C22.4518 7.77752 22.3095 7.06211 22.0329 6.39464C21.7564 5.72718 21.351 5.12082 20.84 4.61Z"
              stroke="currentColor" strokeWidth="2" />
          </svg>
          My Wishlist
        </h1>
        {!isEmpty && (
          <span className="item-count">{getTotalItems()} items</span>
        )}
      </div>


      <div className="wishlist-content">
        {isEmpty ? <EmptyWishlist /> : <FilledWishList />}
      </div>
    </div>
  );
};


export default WishList;
